import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle, RefreshCw, Database } from "lucide-react";
import { adminApi } from "@/admin/api";
import { cn } from "@/lib/utils";

export default function AdminHealth() {
  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["admin-health-full"],
    queryFn: async () => (await adminApi.get("/health")).data,
    staleTime: 30_000,
  });
  const probes = data?.probes || [];
  const mongo = data?.mongo || {};

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="flex items-start justify-between">
        <div>
          <div className="mono uppercase text-[11px] tracking-widest text-totem-textDim">Admin</div>
          <h1 className="display-heading text-3xl">System health</h1>
        </div>
        <button
          onClick={() => refetch()}
          className="mono uppercase text-xs px-3 py-1.5 rounded border border-totem-border hover:border-totem-green/60 inline-flex items-center gap-1"
        >
          <RefreshCw size={12} className={isRefetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <section>
        <div className="label-caps mb-2">Upstream probes</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {isLoading && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="totem-card h-24 animate-pulse" />
          ))}
          {probes.map((p) => {
            const ok = p.status === "success";
            return (
              <div key={p.name} className={cn("totem-card p-4", ok ? "border-totem-green/30" : "border-totem-red/40")}>
                <div className="flex items-center justify-between mb-2">
                  <div className="mono uppercase text-xs">{p.name}</div>
                  {ok ? (
                    <CheckCircle2 className="text-totem-green" size={16} />
                  ) : (
                    <XCircle className="text-totem-red" size={16} />
                  )}
                </div>
                {ok ? (
                  <>
                    <div className="display-heading text-xl">{p.latency_ms}<span className="text-sm text-totem-textDim ml-1">ms</span></div>
                    {p.sample_size !== null && p.sample_size !== undefined && (
                      <div className="mono text-[10px] uppercase text-totem-textDim mt-1">
                        {p.sample_size} records
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-totem-red mono truncate">{p.error}</div>
                )}
                <div className="mono text-[9px] uppercase text-totem-textFaint mt-2">
                  {p.checked_at ? new Date(p.checked_at).toLocaleString() : ""}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <div className="label-caps mb-2 flex items-center gap-2">
          <Database size={12} /> Mongo collections
        </div>
        <div className="totem-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-totem-surface">
              <tr>
                <th className="text-left px-3 py-2 label-caps">Collection</th>
                <th className="text-right px-3 py-2 label-caps">Documents</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(mongo).map(([k, v]) => (
                <tr key={k} className="border-t border-totem-borderSoft">
                  <td className="px-3 py-2 mono">{k}</td>
                  <td className="px-3 py-2 text-right mono font-semibold">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
